import { useLayoutEffect, useRef, useState } from 'react'

import { useOutsideClick } from 'aem-libs-react-ts/src/hooks/useOutsideClick'

import { screenBorderWidth, tipLeftBasePosition, tipWidth } from './constants'

interface VisibilityState {
  isVisible: boolean
  persist: boolean
}

export const useTipPosition = (tipId: string) => {
  const [leftPosition, setLeftPosition] = useState<number | null>(null)
  const isPositioned = useRef(false)

  const tipMessageRef = useOutsideClick<HTMLDivElement, VisibilityState>({
    onClick: (_, e, node) => {
      const targetNode = e?.target as HTMLElement
      if (targetNode.getAttribute('id') === tipId) {
        return
      }
      if (node?.hasAttribute('data-keep-visible')) {
        node.removeAttribute('data-keep-visible')
        node.setAttribute('aria-hidden', 'true')
      }
    }
  })

  const resolveLeftPosition = (tipTextElementRects: DOMRect) => {
    const leftRectPosition = tipTextElementRects.left

    // tip is too left
    if (leftRectPosition < 0) {
      return tipLeftBasePosition + Math.abs(leftRectPosition) + screenBorderWidth
    }

    const maxLeftPosition = window.screen.width - tipWidth

    // tip is too right
    if (leftRectPosition > maxLeftPosition) {
      return maxLeftPosition - leftRectPosition - Math.abs(tipLeftBasePosition) - screenBorderWidth
    }

    return tipLeftBasePosition
  }

  const resolveVerticalOrientation = (tipTextElementRects: DOMRect): 'top' | 'bottom' => {
    return tipTextElementRects.top < 0 ? 'bottom' : 'top'
  }

  useLayoutEffect(() => {
    if (!tipMessageRef.current || isPositioned.current) {
      return
    }

    isPositioned.current = true

    const tipTextElementRects = tipMessageRef.current.getBoundingClientRect()
    const leftPosition = resolveLeftPosition(tipTextElementRects)
    const verticalOrientation = resolveVerticalOrientation(tipTextElementRects)

    tipMessageRef.current.setAttribute('data-vertical-orientation', verticalOrientation)
    setLeftPosition(leftPosition)
  }, [tipMessageRef.current])

  return {
    tipMessageRef,
    leftPosition
  }
}
